/**
 * provenanceOverallStatus — shared pure helper.
 *
 * Derives the single trust-chip label from the panel's computed signal values.
 * Called by BOTH the TrustChip (VehicleDetail title row) and ProvenancePanel
 * (defaultOpen prop) so the two can never drift.
 *
 * States:
 *   - 'verified-attested'  — integrity + seal + attestation all ok
 *   - 'verified'           — integrity + seal ok, attestation none
 *   - 'failed'             — crypto failure (force-opens panel); beats dispute
 *   - 'contested'          — server upheld a dispute; real-world identity in question
 *   - 'under-review'       — a dispute is filed, Absolute Pinks is reviewing
 *   - 'none'               — no bundle (no record yet)
 *   - 'checking'           — loading or actively verifying
 *   - 'unavailable'        — bundle fetch error
 *
 * Precedence order (high → low):
 *   1. error       → 'unavailable'
 *   2. loading     → 'checking'
 *   3. !hasBundle  → 'none'
 *   4. verifying / verifyError → 'checking'
 *   5. crypto fail → 'failed'      (tampered record beats dispute status)
 *   6. contested   → 'contested'
 *   7. under-review → 'under-review'
 *   8. all ok + attestation → 'verified-attested'
 *   9. all ok      → 'verified'
 *  10. fallback    → 'checking'
 *
 * Color rule: green ('verified' | 'verified-attested'), red ('failed' | 'contested'),
 * neutral everything else. NEVER orange. No pink.
 */

export type ProvenanceOverallStatus =
  | 'verified-attested'
  | 'verified'
  | 'failed'
  | 'contested'
  | 'under-review'
  | 'none'
  | 'checking'
  | 'unavailable';

export interface ProvenanceStatusInputs {
  loading: boolean;
  error: Error | null;
  /** Whether the proof bundle exists (even before verification completes). */
  hasBundle: boolean;
  /** Whether the async verification is actively running. */
  verifying: boolean;
  /** Non-null only after verification completes. */
  integrityStatus: 'ok' | 'fail' | 'none';
  sealStatus: 'ok' | 'fail' | 'none';
  attestationVerifyStatus: 'ok' | 'fail' | 'none';
  /** Whether there is any verification error (crypto error, not bundle error). */
  verifyError: string | null;
  /**
   * Mapped from the vehicle doc's wire field `disputeStatus`.
   * Wire value 'underReview' is mapped to 'under-review' before being passed here.
   * Absent from the vehicle doc is equivalent to 'none'.
   */
  disputeState?: 'none' | 'under-review' | 'contested';
}

export function provenanceOverallStatus(inputs: ProvenanceStatusInputs): ProvenanceOverallStatus {
  const {
    loading,
    error,
    hasBundle,
    verifying,
    integrityStatus,
    sealStatus,
    attestationVerifyStatus,
    verifyError,
    disputeState = 'none',
  } = inputs;

  // 1. Bundle fetch error.
  if (error) return 'unavailable';

  // 2. Loading the bundle from the API.
  if (loading) return 'checking';

  // 3. No bundle exists yet.
  if (!hasBundle) return 'none';

  // 4. Bundle exists but verification is in flight (or had a crypto error).
  if (verifying || verifyError) return 'checking';

  // Verification results not yet available.
  if (integrityStatus === 'none' && sealStatus === 'none') return 'checking';

  // 5. Crypto failure — a tampered/invalid record cannot be "under review".
  //    'failed' takes precedence over dispute state so the UI never softens
  //    a cryptographic failure into a dispute label.
  if (integrityStatus === 'fail' || sealStatus === 'fail' || attestationVerifyStatus === 'fail') {
    return 'failed';
  }

  // 6. Dispute upheld — real-world identity is in question, crypto is intact.
  if (disputeState === 'contested') return 'contested';

  // 7. Dispute filed but not yet resolved.
  if (disputeState === 'under-review') return 'under-review';

  // 8-9. All checks passed.
  if (integrityStatus === 'ok' && sealStatus === 'ok') {
    if (attestationVerifyStatus === 'ok') return 'verified-attested';
    return 'verified';
  }

  // 10. Fallback (shouldn't happen — integrity/seal can only be ok/fail/none).
  return 'checking';
}
